import React, { memo, useMemo } from 'react'
import { CampaignTaskElements } from 'services/api/campaignTemplates'
import { TemplateDetailSchema } from '../TemplateDetail'
import { FormikContextType, useFormikContext } from 'formik'
import Text from 'particles/Text'
import { FieldContainer, StickerTextContainer, TemplateFormContainer } from './style'
import { useNewTranslation } from 'hooks'
import { FormikRichInfoListGroup } from 'particles/TextInputs/TextList/RichInfoListGroup'
import { FormikTextListGroup } from 'particles/TextInputs/TextList/Group'
import { FormikRadioButtonGroup } from 'particles/RadioButton/Group'
import Field from 'components/Shared/Fields'
import { UploadType } from 'services/api/uploads'
import ActionButton, { ErrorTasks } from './Actions'
import { FormikTextField } from 'particles/TextInputs/TextField'
import { ElementKeys, getElementsOptions } from '../TemplateDetail/options'
import ReminderAlert from 'particles/Alert/ReminderAlert'
import { useSelector } from 'react-redux'
import { selectCountrySetting } from 'state/constants'
import { selectTemplateDetail } from 'state/campaignTemplates'
import { COUNTRY_ENUM } from 'services/api/constants'
import { selectEditingCampaignCountry } from 'state/campaigns'
import { TFunction } from 'i18next'
import { fieldIdentifiers } from 'components/Shared/Comment'
import TemplateInputFieldContainer from 'components/Shared/Permission/TemplateInputFieldContainer'
import EditTemplateRestricted from 'components/Shared/Permission/EditTemplateRestricted'
import { OnFormikValueChangeProps } from '../OnFormikValueChange'
import InputField from 'components/Shared/Fields'
import SaveChanges from '../SaveChanges'
import { TemplateFilterSchema } from '../TemplateSelector'
import _ from 'lodash'
import { AcceptType } from 'particles/FileUploader'
import { FormikNumberInput } from 'particles/NumberInput'
import { usePermission } from 'hooks/permissions'
import { CampaignManagement } from 'components/Shared/Permission/enums'

interface TemplateTaskProps {
    elementKey: ElementKeys
    field_keys: (keyof CampaignTaskElements)[]
    onNext?: (isCompleted: boolean, isError: boolean) => void
    onBack?: () => void
    onSubmit?: () => void
    validateSpecificField: OnFormikValueChangeProps['validateSpecificField']
    updateCurrentStepStatus: (isCompleted: boolean, isError: boolean) => void
    showError?: boolean
    taskOrder: number
    templateUuid: string | null
    isDisableNavigation: boolean
    openWarningClickToast?: () => void
    identifierPrefix: string
    campaignTemplateStepKey: ElementKeys
    onSaveData: (fieldsValues: Partial<TemplateDetailSchema>, showSuccessToast: boolean) => void
    onShowError: VoidFunction
    fields: (keyof TemplateDetailSchema)[] | (keyof TemplateFilterSchema)[]
}

const TemplateTask = ({
    elementKey,
    field_keys,
    taskOrder,
    showError,
    identifierPrefix,
    onSaveData,
    fields,
    onShowError,
    ...props
}: TemplateTaskProps) => {
    const [t] = useNewTranslation()
    const formikContext = useFormikContext<TemplateDetailSchema>()
    const elementOptions = getElementsOptions(t)

    const templateDetail = useSelector(selectTemplateDetail)
    const countrySetting = useSelector(selectCountrySetting((templateDetail?.country as COUNTRY_ENUM) || null))
    const editingCampaignCountry = useSelector(selectEditingCampaignCountry)
    const isEditableDefaultTag = usePermission(CampaignManagement.EditDefaultTags)

    const repostAlertLabel: Partial<Record<ElementKeys, Label>> = {
        ig_story_repost: {
            title: t('campaign.template.igStoryRepost.infoReminder.title'),
            subtitle: t('campaign.template.igStoryRepost.infoReminder.subText'),
        },
        tiktok_repost: {
            title: t('campaign.template.igStoryRepost.infoReminder.title'),
            subtitle: t('campaign.template.tiktokRepost.infoReminder.subText'),
        },
    }
    const currencySign = useMemo(() => {
        if (!countrySetting) return editingCampaignCountry
        return countrySetting.currency.sign
    }, [editingCampaignCountry, countrySetting])

    const generateAlertComponent = () => {
        if (!repostAlertLabel.hasOwnProperty(elementKey)) {
            return
        }

        return (
            <ReminderAlert
                title={repostAlertLabel[elementKey]?.title || ''}
                subtext={repostAlertLabel[elementKey]?.subtitle}
                form="info"
                width="100%"
            />
        )
    }

    const tasksComponent = useMemo(
        () =>
            field_keys.map(key => (
                <TemplateInputFieldContainer key={key} checkForLiveEditBriefAndDeadline>
                    {getTaskField({
                        key,
                        order: taskOrder,
                        formikContext,
                        taskType: elementKey,
                        showError,
                        currencySign,
                        identifierPrefix,
                        country: (templateDetail?.country as COUNTRY_ENUM) ?? editingCampaignCountry,
                        t,
                        isEditableDefaultTag,
                    })}
                </TemplateInputFieldContainer>
            )),
        [taskOrder, formikContext, elementKey, field_keys],
    )

    const onSave = (onSuccessCallback: VoidFunction) => {
        props.validateSpecificField(fields).then(errors => {
            const noError = Object.keys(errors).length === 0
            if (noError) {
                const fieldsValues = _.pick(formikContext.values, fields)
                const validFieldsValues = _.omitBy(fieldsValues, _.isNull)

                onSaveData(validFieldsValues, true)
                onSuccessCallback()
            } else {
                onShowError()
            }
        })
    }

    return (
        <>
            <TemplateFormContainer>
                <Text scale="H4">{elementOptions[elementKey].title}</Text>
                <FieldContainer>
                    {generateAlertComponent()}
                    {tasksComponent}
                </FieldContainer>

                <EditTemplateRestricted>
                    <ActionButton fieldNames={field_keys} taskOrder={taskOrder} {...props} />
                </EditTemplateRestricted>
            </TemplateFormContainer>
            <SaveChanges onSave={onSave} checkForLiveEditBriefAndDeadline />
        </>
    )
}

export default memo(TemplateTask)

interface GetTaskFieldParam {
    key: keyof CampaignTaskElements
    order: number
    formikContext: FormikContextType<TemplateDetailSchema>
    taskType: ElementKeys
    showError?: boolean
    currencySign: string | null
    identifierPrefix: string
    country?: COUNTRY_ENUM | null
    t: TFunction
    isEditableDefaultTag: boolean
}

const getTaskField = ({
    key,
    order,
    formikContext,
    taskType,
    showError,
    currencySign,
    identifierPrefix,
    country,
    t,
    isEditableDefaultTag,
}: GetTaskFieldParam) => {
    const { values, errors } = formikContext
    const namePrefix = `task_elements.${order}.`
    const taskError = errors.task_elements?.[order]
    const wrongFormatCopy = t('campaign.template.profileTag.errorMessage.wrongFormate')
    const fieldLabelMap = getFieldLabelMap(t)
    const dynamicTitle = fieldLabelMap[key]?.[taskType]?.title
    const dynamicSubTitle = fieldLabelMap[key]?.[taskType]?.subtitle
    const formatErrorMap = getFormatErrorMap(t)
    const isFormatError = !!formatErrorMap[key] && formatErrorMap[key] === (taskError as CampaignTaskElements)?.[key]
    switch (key) {
        case 'products': {
            return (
                <TemplateInputFieldContainer checkForLiveEditBriefAndDeadline>
                    {(isReadOnlyProductName: boolean) => (
                        <TemplateInputFieldContainer>
                            {(isReadOnlyByStatus: boolean) => (
                                <FormikRichInfoListGroup
                                    mainColumnLabel={t('campaign.template.productDetails.column1.placeholder.text')}
                                    secondaryColumnLabel={t('campaign.template.productDetails.column2.title')}
                                    name={`${namePrefix}${key}`}
                                    isReadOnly={isReadOnlyProductName}
                                    mainInputData={{
                                        type: 'text',
                                        props: {
                                            placeholder: t('campaign.template.productDetails.column1.placeholder.text'),
                                        },
                                        widthRatio: '70%',
                                    }}
                                    secondaryInputData={{
                                        type: 'number',
                                        props: {
                                            prefix: currencySign,
                                            placeholder: '0.00',
                                            useRounding: true,
                                            isReadOnly: isReadOnlyByStatus,
                                        },
                                        widthRatio: '30%',
                                    }}
                                    valueKeys={['name', 'price']}
                                    maxItem={5}
                                    maxWidth="100%"
                                    buttonText={t('campaign.template.productDetails.button.text')}
                                    title={t('campaign.template.productDetails.title')}
                                    subtitle={t('campaign.template.productDetails.subText')}
                                    isRequired
                                    isDefault
                                    showError={showError}
                                    tooltip={t('campaign.template.productDetails.tooltips.subText')}
                                    labelSize="l"
                                    identifier={`${identifierPrefix}_${fieldIdentifiers.templateProducts}`}
                                    hideAddButton={isReadOnlyByStatus}
                                />
                            )}
                        </TemplateInputFieldContainer>
                    )}
                </TemplateInputFieldContainer>
            )
        }
        case 'unique_selling_point': {
            return (
                <FormikTextListGroup
                    name={`${namePrefix}${key}`}
                    buttonText={t('campaign.template.generalKeyPoints.button.text')}
                    title={dynamicTitle}
                    subtitle={t('campaign.template.generalKeyPoints.subText')}
                    isRequired
                    isDefault
                    placeholder={t('campaign.template.generalKeyPoints.placeholder.text')}
                    showError={showError}
                    labelSize="l"
                    maxItem={3}
                    identifier={`${identifierPrefix}_${fieldIdentifiers.templateUniqueSellingPoints}`}
                />
            )
        }
        case 'min_purchase_quantity': {
            return (
                <TemplateInputFieldContainer>
                    <FormikRadioButtonGroup
                        options={generateRadioOptions(values['task_elements'][order]?.products?.length || 1)}
                        orientation="horizontal"
                        name={`${namePrefix}${key}`}
                        defaultSelected={1}
                        isRequired
                        title={t('campaign.template.itemNumber.title')}
                        tooltip={t('campaign.template.itemNumber.tooltips.text')}
                        showError={showError}
                        labelSize="l"
                        identifier={`${identifierPrefix}_${fieldIdentifiers.templateMinimumPurchaseQuantity}`}
                    />
                </TemplateInputFieldContainer>
            )
        }
        case 'brand_handles': {
            const showBrandHandlesError = (taskError as ErrorTasks)?.brand_handles === wrongFormatCopy
            return (
                <FormikTextListGroup
                    name={`${namePrefix}${key}`}
                    buttonText={t('campaign.template.profileTag.button.text')}
                    title={dynamicTitle}
                    subtitle={t('campaign.template.generalProfileTags.subText')}
                    placeholder={t('placeholderText.enterProfileTag')}
                    inputIcon="at"
                    inputIconSize="16px"
                    showError={showBrandHandlesError || showError}
                    labelSize="l"
                    dataTestId="test-brand-handles"
                    identifier={`${identifierPrefix}_${fieldIdentifiers.templateBrandHandles}`}
                    isEditableDefaultTag={isEditableDefaultTag}
                />
            )
        }
        case 'caption_hashtags': {
            const showCaptionHashtagError = (taskError as ErrorTasks)?.caption_hashtags === wrongFormatCopy
            return (
                <FormikTextListGroup
                    name={`${namePrefix}${key}`}
                    buttonText={t('button.addHashtag')}
                    title={dynamicTitle}
                    subtitle={t('campaign.template.generalHashtags.subText')}
                    placeholder={t('placeholderText.enterHashtag')}
                    inputIcon="hash"
                    inputIconSize="16px"
                    showError={showCaptionHashtagError || showError}
                    labelSize="l"
                    dataTestId="test-caption-hashtags"
                    identifier={`${identifierPrefix}_${fieldIdentifiers.templateCaptionHashtags}`}
                    isEditableDefaultTag={isEditableDefaultTag}
                />
            )
        }
        case 'example_medias': {
            return (
                <Field.ImageUploadField
                    title={dynamicTitle}
                    subtitle={dynamicSubTitle}
                    name={`${namePrefix}${key}`}
                    presetType={UploadType.CAMPAIGN}
                    maxFiles={10}
                    uploadContainerWidth="100%"
                    maxWidth="100%"
                    acceptTypes={exampleMediaAcceptTypes[taskType]}
                    showError={showError}
                    labelSize="l"
                    dataTestId="test-reference-image-upload"
                    identifier={`${identifierPrefix}_${fieldIdentifiers.templateExampleMedia}`}
                />
            )
        }
        case 'example_media_description': {
            return (
                <Field.TextAreaField
                    name={`${namePrefix}${key}`}
                    label={t('campaign.template.tiktokReferenceCaption.title')}
                    maxTextAreaHeight={80}
                    placeholder={t('campaign.template.generalCaption.placeholder.text')}
                    labelSize="l"
                    identifier={`${identifierPrefix}_${fieldIdentifiers.templateExampleMediaDescription}`}
                />
            )
        }
        case 'purchase_locations': {
            return (
                <FormikTextListGroup
                    name={`${namePrefix}${key}`}
                    buttonText={t('campaign.template.purchaseLocation.button.text')}
                    title={t('campaign.template.purchaseLocation.title')}
                    subtitle={t('campaign.template.purchaseLocation.subText')}
                    isRequired
                    isDefault
                    placeholder={t('campaign.template.purchaseLocation.placeholder.text')}
                    showError={showError}
                    labelSize="l"
                    maxItem={3}
                    identifier={`${identifierPrefix}_${fieldIdentifiers.templatePurchaseLocations}`}
                />
            )
        }
        case 'app_name': {
            return (
                <FormikTextField
                    name={`${namePrefix}${key}`}
                    label={t('campaign.template.downloadAppName.title')}
                    validateByTouch={false}
                    isRequired
                    showError={showError}
                    placeholder={t('campaign.template.downloadAppName.placeholder.text')}
                    labelSize="l"
                    identifier={`${identifierPrefix}_${fieldIdentifiers.templateAppName}`}
                />
            )
        }
        case 'review_platform': {
            return (
                <FormikTextField
                    name={`${namePrefix}${key}`}
                    label={t('campaign.template.step2.reviewTextImageWithApprovalTaskPlatformName.title')}
                    validateByTouch={false}
                    isRequired
                    showError={showError}
                    placeholder={t('campaign.template.step2.reviewTextImageWithApprovalTaskLink.platform.placeholder')}
                    labelSize="l"
                    identifier={`${identifierPrefix}_${fieldIdentifiers.templateReviewPlatform}`}
                />
            )
        }
        case 'download_links': {
            return (
                <FormikRichInfoListGroup
                    name={`${namePrefix}${key}`}
                    labelSize="l"
                    title={t('campaign.template.downloadAppFrom.title')}
                    subtitle={t('campaign.template.downloadAppFrom.subText')}
                    mainColumnLabel={t('campaignCategory.socialMediaPlatformTitle')}
                    secondaryColumnLabel={t('campaign.template.downloadAppFrom.subTitle.link')}
                    valueKeys={['platform', 'link']}
                    hideAddButton
                    isRequired
                    isDraggable={false}
                    showError={isFormatError || showError}
                    fixedItemAmount={2}
                    mainInputData={{
                        widthRatio: '30%',
                        type: 'text',
                        props: {
                            isReadOnly: true,
                            placeholder: t(
                                'campaign.template.step2.reviewTextImageWithApprovalTaskLink.platform.placeholder',
                            ),
                            isDisabled: true,
                        },
                    }}
                    secondaryInputData={{
                        widthRatio: '70%',
                        type: 'text',
                        props: {
                            placeholder: [
                                t('campaign.template.downloadAppFrom.platfrom.appstore.placeholder'),
                                t('campaign.template.downloadAppFrom.platfrom.googlePlayStore.placeholder'),
                            ],
                        },
                    }}
                    identifier={`${identifierPrefix}_${fieldIdentifiers.templateDownloadLinks}`}
                />
            )
        }
        case 'review_link': {
            return (
                <FormikTextField
                    name={`${namePrefix}${key}`}
                    label={t('campaign.template.step2.reviewTextImageWithApprovalTaskPlatformURL.title')}
                    subtext={t('campaign.template.step2.reviewTextImageWithApprovalTaskPlatformURL.subText')}
                    validateByTouch={false}
                    isRequired
                    showError={isFormatError || showError}
                    placeholder={t('campaign.template.step2.reviewTextImageWithApprovalTaskLink.link.placeholder')}
                    labelSize="l"
                    identifier={`${identifierPrefix}_${fieldIdentifiers.templateReviewLink}`}
                    dataTestId="test-review-link-input"
                />
            )
        }
        case 'sample_links': {
            return (
                <FormikTextListGroup
                    name={`${namePrefix}${key}`}
                    buttonText={t('campaign.template.instagramStoryNonUGC.link.button.text')}
                    title={t('campaign.template.instagramStoryNonUGC.link.title')}
                    subtitle={t('campaign.template.instagramStoryNonUGC.link.subText')}
                    isRequired
                    isDefault
                    placeholder={t('campaign.template.instagramStoryNonUGC.link.placeholder.text')}
                    showError={isFormatError || showError}
                    labelSize="l"
                    maxItem={3}
                    identifier={`${identifierPrefix}_${fieldIdentifiers.templateSampleLinks}`}
                />
            )
        }
        case 'sticker_link': {
            return (
                <FormikTextField
                    name={`${namePrefix}${key}`}
                    showError={isFormatError || showError}
                    isRequired
                    validateByTouch={false}
                    label={t('campaign.template.instagramStoryNonUGC.linkStickers.title')}
                    placeholder={t('campaign.template.instagramStoryNonUGC.linkSticker.placeholder.text')}
                    identifier={`${identifierPrefix}_${fieldIdentifiers.templateStickerLink}`}
                />
            )
        }
        case 'customized_sticker_text': {
            const customStickerValue = values.task_elements[order]?.customized_sticker_text
            const isCustomSticker = values.task_elements[order]?.customized_sticker_text_toggle ?? !!customStickerValue
            return (
                <>
                    <FormikRadioButtonGroup
                        name={`${namePrefix}${key}_toggle`}
                        title={t('campaign.template.instagramStoryNonUGC.customisedStickerText.title')}
                        isRequired
                        showError={showError}
                        defaultSelected={isCustomSticker}
                        validateByTouch={false}
                        options={[
                            {
                                label: t('campaign.template.instagramStoryNonUGC.customisedStickerText.optionYes.text'),
                                value: true,
                            },
                            {
                                label: t('campaign.template.instagramStoryNonUGC.customisedStickerText.optionNo.text'),
                                value: false,
                            },
                        ]}
                        identifier={`${identifierPrefix}_${fieldIdentifiers.templateIsCustomizedStickerText}`}
                    />
                    {isCustomSticker && (
                        <StickerTextContainer>
                            <FormikTextField
                                name={`${namePrefix}${key}`}
                                label={t('campaign.template.instagramStoryNonUGC.customisedStickerTextInput.title')}
                                placeholder={t(
                                    'campaign.template.instagramStoryNonUGC.customisedStickerTextInput.placeholder.text',
                                )}
                                isRequired
                                validateByTouch={false}
                                showError={showError}
                                maxInputLength={46}
                                showInputLength
                                identifier={`${identifierPrefix}_${fieldIdentifiers.templateCustomizedStickerText}`}
                            />
                        </StickerTextContainer>
                    )}
                </>
            )
        }
        case 'shipping_fee': {
            return (
                <TemplateInputFieldContainer>
                    <InputField.NumberInputField
                        title={t('campaign.template.shippingFee.title')}
                        subtitle={t('campaign.template.shippingFee.subtexr')}
                        labelTooltip={t('campaign.template.shippingFee.tooltips')}
                        name={`${namePrefix}${key}`}
                        isRequired
                        placeholder="0.00"
                        decimal
                        currencySign={currencySign}
                        labelSize={'l'}
                        data-testid="test-shipping-fee"
                        country={country}
                        min={0}
                        identifier={`${identifierPrefix}_${fieldIdentifiers.templateShippingFee}`}
                    />
                </TemplateInputFieldContainer>
            )
        }
        case 'follow_platform': {
            return (
                <FormikTextField
                    name={`${namePrefix}${key}`}
                    label={t('campaign.template.step2.followTask.platform.title')}
                    validateByTouch={false}
                    isRequired
                    showError={showError}
                    placeholder={t('campaign.template.step2.reviewTextImageWithApprovalTaskLink.platform.placeholder')}
                    labelSize="l"
                    identifier={`${identifierPrefix}_${fieldIdentifiers.templateFollowPlatform}`}
                />
            )
        }
        case 'follow_platform_link': {
            return (
                <FormikTextField
                    name={`${namePrefix}${key}`}
                    label={t('campaign.template.step2.followTask.URL.title')}
                    subtext={t('campaign.template.step2.followTask.URL.subText')}
                    validateByTouch={false}
                    isRequired
                    showError={isFormatError || showError}
                    placeholder={t('campaign.template.step2.reviewTextImageWithApprovalTaskLink.link.placeholder')}
                    labelSize="l"
                    identifier={`${identifierPrefix}_${fieldIdentifiers.templateFollowPlatformLink}`}
                    dataTestId="test-follow-platform-link-input"
                />
            )
        }
        case 'number_of_videos': {
            return (
                <TemplateInputFieldContainer>
                    <FormikNumberInput
                        label={t('campaign.template.step2.videoCreation.numberOfVideo.title')}
                        subtext={t('campaign.template.step2.videoCreation.numberOfVideo.subtext')}
                        labelTooltip={t('campaign.template.step2.videoCreation.numberOfVideo.tooltips')}
                        name={`${namePrefix}${key}`}
                        isRequired
                        showError={isFormatError || showError}
                        placeholder={t('campaign.template.step2.videoCreation.numberOfVideo.placeholder')}
                        labelSize={'l'}
                        size="m"
                        data-testid="test-number-of-videos"
                        min={1}
                        width="100%"
                        identifier={`${identifierPrefix}_${fieldIdentifiers.templateNumberOfVideo}`}
                    />
                </TemplateInputFieldContainer>
            )
        }
        case 'mandatory_guidelines': {
            return (
                <TemplateInputFieldContainer>
                    <Field.TextAreaField
                        label={dynamicTitle}
                        subtext={t('campaign.template.step2.videoCreation.videoGuidelines.subtext')}
                        name={`${namePrefix}${key}`}
                        labelSize={'l'}
                        data-testid="test-mandatory-guidelines"
                        width="100%"
                        identifier={`${identifierPrefix}_${fieldIdentifiers.templateMandatoryGuidelines}`}
                    />
                </TemplateInputFieldContainer>
            )
        }
        default: {
            return null
        }
    }
}

const generateRadioOptions = (amount: number) => {
    const arr = new Array(amount).fill(1).map((_, index) => ({
        label: (index + 1).toString(),
        value: index + 1,
    }))
    return arr
}

interface Label {
    title: string
    subtitle?: string
}

type FieldLabelMap = (t: TFunction) => Partial<Record<keyof CampaignTaskElements, Partial<Record<ElementKeys, Label>>>>

const getFieldLabelMap: FieldLabelMap = t => ({
    caption_hashtags: {
        ig_reels: {
            title: t('campaign.template.instagramHashtag.title'),
        },
        ig_story_repost: {
            title: t('campaign.template.instagramStoryHashtag.title'),
        },
        tiktok_repost: {
            title: t('campaign.template.instagramHashtag.title'),
        },
        tiktok: {
            title: t('campaign.template.tiktokHashtag.title'),
        },
        ig_post: {
            title: t('campaign.template.instagramHashtag.title'),
        },
        ig_story_non_ugc_sticker_link: {
            title: t('campaign.template.instagramStoryHashtag.title'),
        },
        ig_story_ugc: {
            title: t('campaign.template.instagramStoryHashtag.title'),
        },
    },
    brand_handles: {
        ig_reels: {
            title: t('campaign.template.instagramProfileTag.title'),
        },
        ig_story_repost: {
            title: t('campaign.template.instagramStoryProfileTag.title'),
        },
        tiktok_repost: {
            title: t('campaign.template.tiktokProfileTag.title'),
        },
        tiktok: {
            title: t('campaign.template.tiktokProfileTag.title'),
        },
        ig_post: {
            title: t('campaign.template.instagramProfileTag.title'),
        },
        ig_story_non_ugc_sticker_link: {
            title: t('campaign.template.instagramStoryProfileTag.title'),
        },
        ig_story_ugc: {
            title: t('campaign.template.instagramStoryProfileTag.title'),
        },
    },
    example_medias: {
        ig_reels: {
            title: t('campaign.template.instagramReelReferenceImage.title'),
            subtitle: t('errorMessage.validation.maximumFiles', { maxFiles: 10 }),
        },
        ig_story_repost: {
            title: t('campaign.template.instagramStoryReferenceImage.title'),

            subtitle: t('errorMessage.validation.maximumFiles', { maxFiles: 10 }),
        },
        tiktok: {
            title: t('campaign.template.tiktokReference.title'),

            subtitle: t('errorMessage.validation.maximumFiles', { maxFiles: 10 }),
        },
        purchase: {
            title: t('campaign.template.productImage.title'),
            subtitle: t('campaign.template.productImage.subText'),
        },
        ig_post: {
            title: t('campaign.template.instagramPostReference.title'),
            subtitle: t('errorMessage.validation.maximumFiles', { maxFiles: 10 }),
        },
        ig_story_non_ugc_sticker_link: {
            title: t('campaign.template.instagramStoryReferenceImage.title'),
            subtitle: t('errorMessage.validation.maximumFiles', { maxFiles: 10 }),
        },
        ig_story_ugc: {
            title: t('campaign.template.instagramStoryReferenceImage.title'),
            subtitle: t('errorMessage.validation.maximumFiles', { maxFiles: 10 }),
        },
        photo_creation: {
            title: t('campaign.template.step2.photoCreation.photoReference.title'),
            subtitle: t('campaignEditor.createTask.uploadFilesExampleSubtext', { maxFiles: 10 }),
        },
        video_creation: {
            title: t('campaign.template.step2.videoCreation.sampleVideo.title'),
            subtitle: t('campaignEditor.createTask.uploadFilesExampleSubtext', { maxFiles: 10 }),
        },
    },
    unique_selling_point: {
        ig_reels: {
            title: t('campaign.template.instagramReelKeyPoints.title'),
        },
        tiktok: {
            title: t('campaign.template.tiktokKeyPoints.title'),
        },
        ig_post: {
            title: t('campaign.template.instagramPostKeyPoints.title'),
        },
        review_text_image_approval: {
            title: t('campaign.template.step2.reviewTextImageWithApprovalTaskKeyPoints.title'),
        },
        ig_story_non_ugc_sticker_link: {
            title: t('campaign.template.instagramStoryKeyPoints.title'),
        },
        review_text: {
            title: t('campaign.template.step2.reviewTextImageWithApprovalTaskKeyPoints.title'),
        },
        review_text_approval: {
            title: t('campaign.template.step2.reviewTextImageWithApprovalTaskKeyPoints.title'),
        },
        review_text_purchase: {
            title: t('campaign.template.step2.reviewTextImageWithApprovalTaskKeyPoints.title'),
        },
        ig_story_ugc: {
            title: t('campaign.template.instagramStoryKeyPoints.title'),
        },
        review_text_image_approval_purchase: {
            title: t('campaign.template.step2.reviewTextImageWithApprovalTaskKeyPoints.title'),
        },
        video_creation: {
            title: t('campaign.template.step2.videoCreation.keyPoints.title'),
        },
        photo_creation: {
            title: t('campaign.template.step2.photoCreation.keyPoints.title'),
        },
    },
    mandatory_guidelines: {
        video_creation: {
            title: t('campaign.template.step2.videoCreation.videoGuidelines.title'),
        },
        photo_creation: {
            title: t('campaign.template.step2.photoCreation.photoGuidelines.title'),
        },
    },
})

const getFormatErrorMap = (t: TFunction): Partial<Record<keyof CampaignTaskElements, string>> => ({
    download_links: t('campaign.template.downloadAppFrom.errorMessage.wrongFormate'),
    review_link: t('campaign.template.downloadAppFrom.errorMessage.wrongFormate'),
    caption_hashtags: t('campaign.template.profileTag.errorMessage.wrongFormate'),
    brand_handles: t('campaign.template.profileTag.errorMessage.wrongFormate'),
    sample_links: t('campaign.template.step2.reviewTextImageWithApprovalTask.errorMessage.wrongFormat'),
    sticker_link: t('campaign.template.step2.reviewTextImageWithApprovalTask.errorMessage.wrongFormat'),
    follow_platform_link: t('campaign.template.downloadAppFrom.errorMessage.wrongFormate'),
    number_of_videos: t('errorMessage.validationPostiveInteger'),
})

const exampleMediaAcceptTypes: Partial<Record<ElementKeys, AcceptType[]>> = {
    video_creation: ['video'],
    photo_creation: ['image'],
}
